import moment, { JUnitOfTime } from 'moment-jalaali';
import { NumberOrNull } from 'store/entity/types';
import { englishNumber, convertDigitToProperFormat, isANumber } from './number';
import PeriodTypeSelectorMessages from 'components/FileManager/ItemsViewer/Survey/SurveyEditor/UniqueQuestion/PeriodTypeSelector/Messages';
import { TimeScale } from 'store/survey/surveyTypes';
import i18n, { getLangBaseOnLocal } from '../i18n';
import { ILocal } from '../components/Chart/BaseDatePicker';

moment.loadPersian({ dialect: 'persian-modern' });

export const monthToTimestamp = (
  monthNumber: number,
  local: ILocal = 'fa_IR@calendar=persian'
): number => {
  const yearFormat = local === 'fa_IR@calendar=persian' ? 'jYYYY-jM' : 'YYYY-M';
  const year = Math.floor(monthNumber / 12);
  const month = (monthNumber % 12) + 1;
  const date =
    local === 'fa_IR@calendar=persian'
      ? moment(`${year}-${month}`, yearFormat)
      : moment(`${year}-${month}`, yearFormat).locale('en');
  return date.valueOf();
};

export const endOfDate = <T extends boolean = false>(
  date: Date,
  scale: TimeScale,
  returnTimestamp?: T,
  local: 'fa' | 'en' = 'fa'
): T extends true ? number : Date => {
  let convertedDate: Date = new Date();
  const mnt = local === 'fa' ? moment(date) : moment(date).locale('en');
  const yearFormat = local === 'en' ? 'YYYY' : 'jYYYY';
  const yearMonthFormat = local === 'en' ? 'YYYY-MM' : 'jYYYY-jMM';
  const mFormat = local === 'en' ? 'M' : 'jM';
  const month = local === 'en' ? 'month' : 'jMonth';
  const year = local === 'en' ? 'year' : 'jYear';
  if (scale === 'day') {
    convertedDate = mnt.endOf('day').toDate();
  } else if (scale === 'week') {
    convertedDate = mnt
      .endOf('week')
      // .subtract(1, 'day')
      .toDate();
  } else if (scale === 'month') {
    convertedDate = mnt.endOf(month as JUnitOfTime).toDate();
  } else if (scale === 'season') {
    const thisMonthNumber = englishNumber(mnt.format(mFormat));
    const endMonthOfSeason =
      (Math.floor((Number(thisMonthNumber) - 1) / 3) + 1) * 3;
    const thisYear = englishNumber(mnt.format(yearFormat));
    convertedDate = (
      local === 'fa'
        ? moment(`${thisYear}-${endMonthOfSeason}`, yearMonthFormat)
        : moment(`${thisYear}-${endMonthOfSeason}`, yearMonthFormat).locale(
            'en'
          )
    )
      .endOf(month as JUnitOfTime)
      .toDate();
  } else if (scale === 'twoSeason') {
    const thisMonthNumber = englishNumber(mnt.format(mFormat));
    const endMonthOfTwoSeason = Number(thisMonthNumber) <= 6 ? 6 : 12;
    const thisYear = englishNumber(mnt.format(yearFormat));
    convertedDate = (
      local === 'fa'
        ? moment(`${thisYear}-${endMonthOfTwoSeason}`, yearMonthFormat)
        : moment(`${thisYear}-${endMonthOfTwoSeason}`, yearMonthFormat).locale(
            'en'
          )
    )
      .endOf(month as JUnitOfTime)
      .toDate();
  } else if (scale === 'year') {
    convertedDate = mnt.endOf(year as JUnitOfTime).toDate();
  }
  return (
    returnTimestamp
      ? local === 'fa'
        ? moment(convertedDate).valueOf()
        : moment(convertedDate).locale('en').valueOf()
      : convertedDate
  ) as any;
};

export const startOfDate = <T extends boolean = false>(
  date: Date,
  scale: TimeScale,
  returnTimestamp?: T,
  local: 'fa' | 'en' = 'fa'
): T extends true ? number : Date => {
  const mnt = local === 'fa' ? moment(date) : moment(date).locale('en');
  const yearFormat = local === 'en' ? 'YYYY' : 'jYYYY';
  const yearMonthFormat = local === 'en' ? 'YYYY-MM' : 'jYYYY-jMM';
  const mmFormat = local === 'en' ? 'MM' : 'jMM';
  const mFormat = local === 'en' ? 'M' : 'jM';
  const month = local === 'en' ? 'month' : 'jMonth';
  const year = local === 'en' ? 'year' : 'jYear';

  let convertedDate: Date = new Date();
  if (scale === 'day') {
    convertedDate = mnt.startOf('day').toDate();
  } else if (scale === 'week') {
    convertedDate = mnt
      .startOf('week')
      // .subtract(1, 'day')
      .toDate();
  } else if (scale === 'month') {
    convertedDate = mnt.startOf(month as JUnitOfTime).toDate();
  } else if (scale === 'season') {
    const thisMonthNumber = englishNumber(mnt.format(mmFormat));
    const firstMonthOfSeason =
      (Math.floor((Number(thisMonthNumber) - 1) / 3) + 1) * 3 - 2;
    const thisYear = englishNumber(mnt.format(yearFormat));
    convertedDate = (
      local === 'fa'
        ? moment(`${thisYear}-${firstMonthOfSeason}`, yearMonthFormat)
        : moment(`${thisYear}-${firstMonthOfSeason}`, yearMonthFormat).locale(
            'en'
          )
    )
      .startOf(month as JUnitOfTime)
      .toDate();
  } else if (scale === 'twoSeason') {
    const thisMonthNumber = englishNumber(mnt.format(mFormat));
    const firstMonthOfTwoSeason = Number(thisMonthNumber) <= 6 ? 1 : 7;
    const thisYear = englishNumber(moment(date).format(yearFormat));
    convertedDate = moment(
      `${thisYear}-${firstMonthOfTwoSeason}`,
      yearMonthFormat
    )
      .startOf(month as JUnitOfTime)
      .toDate();
  } else if (scale === 'year') {
    convertedDate = mnt.startOf(year as JUnitOfTime).toDate();
  }
  return (
    returnTimestamp
      ? local === 'fa'
        ? moment(convertedDate).valueOf()
        : moment(convertedDate).locale('en').valueOf()
      : convertedDate
  ) as any;
};

export const timeInMinutes = (time: number) => {
  const firstOfDay = moment(time).startOf('day').valueOf();
  const minutes = (time - firstOfDay) / (60 * 1000);
  return minutes;
};

export const jMonthIndex = (year: number, thisYearMonthIndex: number) =>
  year * 12 + thisYearMonthIndex - 1;

export const monthIndexToTimestamp = (index: number, local: ILocal) => {
  const year = Math.floor(index / 12);
  const month = (index % 12) + 1;

  const dateOfMonth =
    local === 'fa_IR@calendar=persian'
      ? moment(`${year}-${month}-01`, 'jYYYY-jM-jDD').toDate()
      : moment(`${year}-${month}-01`, 'YYYY-M-DD').locale('en').toDate();
  return endOfDate(dateOfMonth, 'month', true, getLangBaseOnLocal(local));
};

export const smartDateString = (
  date: Date | number | null | undefined,
  includeMinutes: boolean = false,
  includeYear: boolean = false,
  includeDay: boolean = true,
  xformat: string | undefined = undefined,
  forceIncludeMinitues: boolean = false
) => {
  if (date === null || date === undefined || Number.isNaN(date))
    return i18n.t('unknown', { ns: 'survey' });
  const language = i18n.language;
  let format = language === 'fa' ? 'jD jMMMM' : 'D MMMM';
  const sameYear =
    language === 'fa'
      ? moment(Date()).jYear() === moment(date).jYear()
      : moment(Date()).locale('en').year() === moment(date).locale('en').year();
  if (!sameYear || includeYear)
    format = language === 'fa' ? 'jD jMMMM jYYYY' : 'MMMM D, YYYY';
  if (!includeDay) format = format.replace(/j?D,?\s+/, '');
  if ((sameYear && includeMinutes) || forceIncludeMinitues) {
    format = format + ' | HH:mm';
  }
  if (xformat) format = xformat;
  return language === 'fa'
    ? convertDigitToProperFormat(moment(date).format(format))
    : convertDigitToProperFormat(moment(date).locale('en').format(format));
};
// export const smartDateDuration = (
//   from?: Date | NumberOrNull,
//   to?: Date | NumberOrNull,
//   includeMinutes: boolean = false,
//   includeDays: boolean = true,
//   includeYear: boolean = false,
//   timeScale?: TimeScale
// ) => {
//   const lang = i18n.language;
//   if (typeof from === 'number' && from) from = new Date(from);
//   if (typeof to === 'number' && to) to = new Date(to);
//   const mFrom = from
//     ? lang === 'fa'
//       ? moment(from)
//       : moment(from).locale('en')
//     : undefined;
//   let mTo = to
//     ? lang === 'fa'
//       ? moment(to)
//       : moment(to).locale('en')
//     : undefined;
//   const sameYear =
//     mFrom &&
//     mTo &&
//     (lang === 'fa'
//       ? mFrom.jYear() === mTo.jYear()
//       : mFrom.year() === mTo.year());
//   if (
//     (timeScale === 'season' ||
//       timeScale === 'twoSeason' ||
//       timeScale === 'year') &&
//     mFrom &&
//     mTo
//   ) {
//     mTo = mTo.add(-1, 'day');
//     const getName =
//       timeScale === 'season'
//         ? PeriodTypeSelectorMessages.getSeason
//         : timeScale === 'twoSeason'
//           ? (PeriodTypeSelectorMessages.getTwoSeasonNameNumber as any)
//           : () => '';
//     const start = getName(
//       lang === 'fa' ? mFrom.jMonth() + 1 : mFrom.month() + 1
//     );
//     const end = getName(lang === 'fa' ? mTo.jMonth() + 1 : mTo.month() + 1);
//     let ret = start;
//     if (!sameYear)
//       ret += ` ${convertDigitToProperFormat(
//         lang === 'fa' ? mFrom.jYear() : mFrom.year()
//       )}`;
//     if (!sameYear || start !== end) ret += ' تا ' + end;
//     ret += ` ${convertDigitToProperFormat(mTo.jYear())}`;
//     return ret;
//   }
//   let sameMonth;
//   let sameDay;
//   if (i18n.language === 'fa') {
//     sameMonth = sameYear && mFrom!.jMonth() === mTo!.jMonth();
//     sameDay = sameMonth && mFrom!.jDate() === mTo!.jDate();
//   } else {
//     sameMonth = sameYear && mFrom!.month() === mTo!.month();
//     sameDay = sameMonth && mFrom!.date() === mTo!.date();
//   }
//
//   if (sameDay) {
//     if (!includeMinutes)
//       return smartDateString(from!, includeMinutes, includeYear, includeDays);
//     else {
//       const currentYear =
//         i18n.language === 'fa'
//           ? moment(Date()).jYear() === moment(from).jYear()
//           : moment(Date()).year() === moment(from).year();
//       return (
//         smartDateString(from!, includeMinutes, false, includeDays) +
//         (currentYear
//           ? i18n.t('until', {
//               ns: 'general',
//               time: convertDigitToProperFormat(moment(to).format('HH:mm')),
//             })
//           : '')
//       );
//     }
//   }
//   if (sameMonth) {
//     const startDay = convertDigitToProperFormat(
//       (i18n.language === 'fa'
//         ? moment(from)
//         : moment(from).locale('en')
//       ).format(i18n.language === 'fa' ? 'jD' : 'D')
//     );
//     const toDate = smartDateString(to!, false, includeYear, true);
//     return i18n.t('fromUntil', {
//       ns: 'general',
//       time1: startDay,
//       time2: toDate,
//     });
//   }
//   if (sameYear) {
//     const startDay = convertDigitToProperFormat(
//       moment(from).format(
//         i18n.language === 'fa'
//           ? `${includeDays ? 'jD ' : ''}jMMMM`
//           : `${includeDays ? 'D ' : ''}MMMM`
//       )
//     );
//     const toDate = smartDateString(to!, false, includeYear, includeDays);
//     return i18n.t('fromUntil', {
//       ns: 'general',
//       time1: startDay,
//       time2: toDate,
//     });
//   }
//   if (from && to)
//     return i18n.t('fromUntilTo', {
//       ns: 'general',
//       time1: smartDateString(from, false, true, includeDays),
//       time2: smartDateString(to, false, true, includeDays),
//     });
//   if (from)
//     return i18n.t('from', {
//       ns: 'general',
//       time: smartDateString(from, includeMinutes, includeYear, includeDays),
//     });
//   if (to)
//     return i18n.t('until', {
//       ns: 'general',
//       time: smartDateString(to, includeMinutes, includeYear, includeDays),
//     });
//   return '';
// };

export const smartDateDuration = (
  from?: Date | NumberOrNull,
  to?: Date | NumberOrNull,
  includeMinutes: boolean = false,
  includeDays: boolean = true,
  includeYear: boolean = false,
  timeScale?: TimeScale
) => {
  const lang = i18n.language;

  const createMoment = (time: Date | undefined) => {
    if (!time) return undefined;
    return lang === 'fa' ? moment(time) : moment(time).locale('en');
  };

  if (typeof from === 'number' && from) from = new Date(from);
  if (typeof to === 'number' && to) to = new Date(to);

  const mFrom = createMoment(from as Date);
  let mTo = createMoment(to as Date);

  const sameYear =
    mFrom &&
    mTo &&
    (lang === 'fa'
      ? mFrom.jYear() === mTo.jYear()
      : mFrom.year() === mTo.year());

  if (
    (timeScale === 'season' ||
      timeScale === 'twoSeason' ||
      timeScale === 'year') &&
    mFrom &&
    mTo
  ) {
    mTo = mTo.add(-1, 'day');
    const getName =
      timeScale === 'season'
        ? lang === 'fa'
          ? PeriodTypeSelectorMessages.getSeason
          : PeriodTypeSelectorMessages.getEnSeason
        : timeScale === 'twoSeason'
          ? (PeriodTypeSelectorMessages.getTwoSeasonNameNumber as any)
          : () => '';

    const start = getName(
      lang === 'fa' ? mFrom.jMonth() + 1 : mFrom.month() + 1
    );
    const end = getName(lang === 'fa' ? mTo.jMonth() + 1 : mTo.month() + 1);
    let ret = start;

    if (!sameYear)
      ret += ` ${convertDigitToProperFormat(lang === 'fa' ? mFrom.jYear() : mFrom.year())}`;

    if (!sameYear || start !== end)
      ret += i18n.t('simpleUntil', { ns: 'general' }) + end;
    ret += ` ${convertDigitToProperFormat(lang === 'fa' ? mTo.jYear() : mTo.year())}`;

    return ret;
  }

  let sameMonth;
  let sameDay;
  if (lang === 'fa') {
    sameMonth = sameYear && mFrom!.jMonth() === mTo!.jMonth();
    sameDay = sameMonth && mFrom!.jDate() === mTo!.jDate();
  } else {
    sameMonth = sameYear && mFrom!.month() === mTo!.month();
    sameDay = sameMonth && mFrom!.date() === mTo!.date();
  }

  if (sameDay) {
    if (!includeMinutes)
      return smartDateString(from!, includeMinutes, includeYear, includeDays);
    else {
      const currentYear =
        lang === 'fa'
          ? moment(Date()).jYear() === moment(from).jYear()
          : moment(Date()).year() === moment(from).year();
      return `${smartDateString(from!, includeMinutes, false, includeDays)} ${
        currentYear
          ? (i18n.t('until', {
              ns: 'general',
              time: convertDigitToProperFormat(moment(to).format('HH:mm')),
            }) as string)
          : ''
      }`;
    }
  }

  if (sameMonth) {
    const startDay = convertDigitToProperFormat(
      includeMinutes
        ? smartDateString(from!, includeMinutes, includeYear, true)
        : (lang === 'fa' ? moment(from) : moment(from).locale('en')).format(
            lang === 'fa' ? 'jD' : 'D'
          )
    );
    const toDate = smartDateString(to!, includeMinutes, includeYear, true);
    return i18n.t('fromUntil', {
      ns: 'general',
      time1: startDay,
      time2: toDate,
    });
  }

  if (sameYear) {
    const startDay = includeMinutes
      ? smartDateString(from!, includeMinutes, includeYear, true)
      : convertDigitToProperFormat(
          (lang === 'fa' ? moment(from) : moment(from).locale('en')).format(
            lang === 'fa'
              ? `${includeDays ? 'jD ' : ''}jMMMM`
              : `${includeDays ? 'D ' : ''}MMMM`
          )
        );
    const toDate = smartDateString(
      to!,
      includeMinutes,
      includeYear,
      includeDays
    );
    return i18n.t('fromUntil', {
      ns: 'general',
      time1: startDay,
      time2: toDate,
    });
  }

  if (from && to)
    return i18n.t('fromUntilTo', {
      ns: 'general',
      time1: smartDateString(from, false, true, includeDays),
      time2: smartDateString(to, false, true, includeDays),
    });

  if (from)
    return i18n.t('from', {
      ns: 'general',
      time: smartDateString(from, includeMinutes, includeYear, includeDays),
    });

  if (to)
    return i18n.t('until', {
      ns: 'general',
      time: smartDateString(to, includeMinutes, includeYear, includeDays),
    });

  return '';
};

export const smartSplitDateAndTimeDuration = (
  from?: Date | number,
  to?: Date | number,
  includeMinutes: boolean = false
) => {
  if (!from && !to) {
    return {
      date: '',
      time: '',
    };
  }

  if (from && !to) {
    return {
      date: smartDateString(from, false),
      time: includeMinutes
        ? convertDigitToProperFormat(moment(from).format('HH:mm'))
        : null,
    };
  }

  if (!from && to) {
    return {
      date: smartDateString(to, false),
      time: includeMinutes
        ? convertDigitToProperFormat(moment(to).format('HH:mm'))
        : null,
    };
  }

  const mFrom = isANumber(from) ? moment(from) : undefined;
  const mTo = isANumber(to) ? moment(to) : undefined;
  const sameYear =
    mFrom && mTo && i18n.language === 'fa'
      ? mFrom.jYear() === mTo.jYear()
      : mFrom?.year() === mTo?.year();
  const sameMonth =
    sameYear &&
    (i18n.language === 'fa'
      ? mFrom?.jMonth() === mTo?.jMonth()
      : mFrom?.month() === mTo?.month());
  const sameDay =
    sameMonth &&
    (i18n.language === 'fa'
      ? mFrom?.jDate() === mTo?.jDate()
      : mFrom?.date() === mTo?.date());
  if (sameDay) {
    return {
      date: smartDateString(from!, false),
      time: includeMinutes
        ? i18n.t('to', {
            ns: 'general',
            time1: convertDigitToProperFormat(moment(from).format('HH:mm')),
            time2: convertDigitToProperFormat(
              moment(to).locale('en').format('HH:mm')
            ),
          })
        : null,
    };
  }
  if (sameMonth) {
    const startDay =
      i18n.language === 'fa'
        ? convertDigitToProperFormat(moment(from).format('jD'))
        : moment(from).locale('en').format('D');
    const toDate = convertDigitToProperFormat(smartDateString(to!, false));
    return {
      date: i18n.t('fromUntilTo', {
        ns: 'general',
        time1: startDay,
        time2: toDate,
      }),
      time: null,
    };
  }
  if (sameYear) {
    const startDay =
      i18n.language === 'fa'
        ? convertDigitToProperFormat(moment(from).format('jD jMMMM'))
        : moment(from).locale('en').format('D MMMM');
    const toDate = convertDigitToProperFormat(smartDateString(to!, false));
    return {
      date: i18n.t('fromUntilTo', {
        ns: 'general',
        time1: startDay,
        time2: toDate,
      }),
      time: null,
    };
  }
  if (from && to)
    return {
      date: i18n.t('until', {
        ns: 'general',
        time: convertDigitToProperFormat(smartDateString(to, false, true)),
      }),
      time: null,
    };
  return { date: '', time: null };
};
